import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Power, Play, Lightning, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const TRIGGERS = [
  "manual", "lead.created", "lead.stage_changed", "order.created",
  "order.status_changed", "invoice.created", "invoice.overdue",
  "customer.created", "followup.completed", "form.submitted", "schedule.daily",
];
const OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "contains", "in", "not_in", "empty", "not_empty"];
const ACTIONS = [
  { type: "create_followup", label: "Create follow-up", fields: [
    { key: "customer_id", label: "Customer ID (or ctx.customer_id)" },
    { key: "next_date", label: "Next date (YYYY-MM-DD)" },
    { key: "next_time", label: "Time (HH:MM)" },
    { key: "channel", label: "Channel", select: ["call", "whatsapp", "email", "meeting"] },
    { key: "notes", label: "Notes", multiline: true },
  ]},
  { type: "send_email", label: "Send email", fields: [
    { key: "to", label: "Recipient (or ctx.email)" },
    { key: "template", label: "Template ID" },
    { key: "subject", label: "Subject" },
    { key: "body", label: "Body", multiline: true },
  ]},
  { type: "send_whatsapp", label: "Send WhatsApp", fields: [
    { key: "to", label: "Recipient (or ctx.mobile)" },
    { key: "template", label: "Template name" },
    { key: "message", label: "Message", multiline: true },
  ]},
  { type: "create_reminder", label: "Create reminder", fields: [
    { key: "collection", label: "Record collection", select: ["leads", "orders", "invoices", "customers"] },
    { key: "record_id", label: "Record ID (or ctx.id)" },
    { key: "reminder_at", label: "Reminder ISO time" },
  ]},
  { type: "notify", label: "Internal notification", fields: [
    { key: "user_id", label: "Target user id (or ctx.owner_id)" },
    { key: "title", label: "Title" },
    { key: "body", label: "Body", multiline: true },
  ]},
  { type: "require_approval", label: "Require approval", fields: [
    { key: "approver_id", label: "Approver user id" },
    { key: "approver_role", label: "Approver role", select: ["organization_owner", "admin", "sales_manager", "accountant"] },
    { key: "title", label: "Approval title" },
    { key: "body", label: "Approval body", multiline: true },
  ]},
  { type: "webhook", label: "Webhook (POST)", fields: [
    { key: "url", label: "URL" },
    { key: "method", label: "Method", select: ["POST", "PUT", "PATCH"] },
  ]},
];

const EMPTY_WF = { name: "", description: "", trigger: "manual", conditions: [], actions: [], enabled: true };

function actionDef(t) { return ACTIONS.find((a) => a.type === t); }

function WorkflowDialog({ open, onOpenChange, initial, onSave }) {
  const [f, setF] = useState(EMPTY_WF);
  useEffect(() => { setF(initial ? Object.assign({}, EMPTY_WF, initial) : EMPTY_WF); }, [initial, open]);
  const upd = (patch) => setF(Object.assign({}, f, patch));
  const updCond = (i, patch) => upd({ conditions: f.conditions.map((c, idx) => idx === i ? Object.assign({}, c, patch) : c) });
  const rmCond = (i) => upd({ conditions: f.conditions.filter((_, idx) => idx !== i) });
  const addCond = () => upd({ conditions: f.conditions.concat([{ field: "", op: "eq", value: "" }]) });
  const updAct = (i, patch) => upd({ actions: f.actions.map((a, idx) => idx === i ? Object.assign({}, a, patch) : a) });
  const updActCfg = (i, key, val) => updAct(i, { config: Object.assign({}, f.actions[i].config || {}, { [key]: val }) });
  const rmAct = (i) => upd({ actions: f.actions.filter((_, idx) => idx !== i) });
  const addAct = () => upd({ actions: f.actions.concat([{ type: "create_followup", config: {} }]) });
  const moveAct = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= f.actions.length) return;
    const next = f.actions.slice();
    const tmp = next[i]; next[i] = next[j]; next[j] = tmp;
    upd({ actions: next });
  };
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    try { await onSave(f); onOpenChange(false); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="workflow-dialog">
        <DialogHeader><DialogTitle>{initial && initial.id ? "Edit workflow" : "New workflow"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">NAME</Label>
              <Input value={f.name} onChange={(e) => upd({ name: e.target.value })} data-testid="workflow-name" />
            </div>
            <div>
              <Label className="text-xs">TRIGGER</Label>
              <Select value={f.trigger} onValueChange={(v) => upd({ trigger: v })}>
                <SelectTrigger data-testid="workflow-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">DESCRIPTION</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => upd({ description: e.target.value })} /></div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">CONDITIONS · all must match ({f.conditions.length})</Label>
              <Button size="sm" variant="outline" onClick={addCond} data-testid="wf-add-condition"><Plus size={12} className="mr-1" />Condition</Button>
            </div>
            {f.conditions.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={"wf-condition-" + i}>
                <Input className="col-span-4 h-8 font-mono text-xs" placeholder="field.path (e.g. order.total)" value={c.field}
                  onChange={(e) => updCond(i, { field: e.target.value })} />
                <Select value={c.op} onValueChange={(v) => updCond(i, { op: v })}>
                  <SelectTrigger className="col-span-2 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="col-span-5 h-8" placeholder="value" value={c.value ?? ""}
                  onChange={(e) => updCond(i, { value: e.target.value })} />
                <button className="col-span-1 text-destructive" onClick={() => rmCond(i)}><Trash size={13} className="mx-auto" /></button>
              </div>
            ))}
            {!f.conditions.length && <div className="text-xs text-muted-foreground italic">No conditions — this workflow runs on every trigger event.</div>}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">ACTIONS · run in order ({f.actions.length})</Label>
              <Button size="sm" variant="outline" onClick={addAct} data-testid="wf-add-action"><Plus size={12} className="mr-1" />Action</Button>
            </div>
            <div className="space-y-3">
              {f.actions.map((a, i) => {
                const def = actionDef(a.type) || ACTIONS[0];
                return (
                  <div key={i} className="border rounded-md p-3 space-y-2 bg-secondary/30" data-testid={"wf-action-" + i}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{i + 1}.</span>
                      <Select value={a.type} onValueChange={(v) => updAct(i, { type: v, config: {} })}>
                        <SelectTrigger className="h-8 flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIONS.map((x) => <SelectItem key={x.type} value={x.type}>{x.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => moveAct(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 rounded hover:bg-secondary">↑</button>
                      <button onClick={() => moveAct(i, 1)} disabled={i === f.actions.length - 1} className="p-1 disabled:opacity-30 rounded hover:bg-secondary">↓</button>
                      <button onClick={() => rmAct(i)} className="p-1 text-destructive rounded hover:bg-destructive/10"><Trash size={13} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {(def.fields || []).map((fld) => (
                        <div key={fld.key} className={fld.multiline ? "col-span-2" : ""}>
                          <Label className="text-[10px]">{fld.label}</Label>
                          {fld.select ? (
                            <Select value={(a.config || {})[fld.key] || ""} onValueChange={(v) => updActCfg(i, fld.key, v)}>
                              <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>{fld.select.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : fld.multiline ? (
                            <Textarea rows={2} value={(a.config || {})[fld.key] || ""}
                              onChange={(e) => updActCfg(i, fld.key, e.target.value)} />
                          ) : (
                            <Input className="h-8" value={(a.config || {})[fld.key] || ""}
                              onChange={(e) => updActCfg(i, fld.key, e.target.value)} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {!f.actions.length && <div className="text-xs text-muted-foreground italic">Add at least one action.</div>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={f.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="workflow-enabled" />
            Enabled
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="workflow-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function statusIcon(s) {
  if (s === "completed") return <CheckCircle size={12} weight="duotone" className="text-emerald-600" />;
  if (s === "rejected") return <XCircle size={12} weight="duotone" className="text-destructive" />;
  if (s === "waiting_approval") return <Clock size={12} weight="duotone" className="text-amber-600" />;
  return <Lightning size={12} weight="duotone" className="text-muted-foreground" />;
}

export default function WorkflowBuilder() {
  const [workflows, setWorkflows] = useState([]);
  const [runs, setRuns] = useState({});     // wfId -> [runs]
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedRuns, setExpandedRuns] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/platform/workflows"); setWorkflows(r.data); }
    catch { toast.error("Failed to load workflows"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/workflows/" + editing.id, payload);
      toast.success("Workflow updated");
    } else {
      await api.post("/platform/workflows", payload);
      toast.success("Workflow created");
    }
    load();
  };
  const toggle = async (wf) => { await api.put("/platform/workflows/" + wf.id + "/toggle"); load(); };
  const remove = async (wf) => {
    if (!window.confirm("Delete workflow \"" + wf.name + "\"?")) return;
    await api.delete("/platform/workflows/" + wf.id);
    toast.success("Deleted"); load();
  };
  const testRun = async (wf) => {
    const raw = window.prompt("Paste JSON context for the test run:", '{"customer_id":"test","email":"test@example.com"}');
    if (!raw) return;
    let ctx = {};
    try { ctx = JSON.parse(raw); } catch { return toast.error("Invalid JSON"); }
    try {
      const r = await api.post("/platform/workflows/" + wf.id + "/test-run", { context: ctx });
      toast.success(`Run ${r.data.status} · ${r.data.run_id?.slice(0, 8) || ""}`);
      openRuns(wf);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Test run failed");
    }
  };
  const openRuns = async (wf) => {
    setExpandedRuns(wf.id);
    const r = await api.get("/workflows/" + wf.id + "/runs");
    setRuns((prev) => Object.assign({}, prev, { [wf.id]: r.data }));
  };

  return (
    <Card className="p-6" data-testid="workflow-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">Automation</div>
          <h3 className="font-heading font-bold text-xl">Workflow Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Trigger → conditions → actions. Chain follow-ups, emails, WhatsApp, reminders, notifications, approvals & webhooks.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="workflow-add">
          <Plus size={14} className="mr-1.5" />New workflow
        </Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="space-y-3">
          {workflows.map((wf) => {
            const isOpen = expandedRuns === wf.id;
            const wfRuns = runs[wf.id] || [];
            return (
              <div key={wf.id} className={"border rounded-lg p-4 " + (wf.enabled ? "" : "opacity-60")} data-testid={"workflow-card-" + wf.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-sm flex items-center gap-2">
                      {wf.name}
                      <span className="text-[10px] uppercase tracking-wider text-primary/80 border border-primary/30 px-1 rounded font-mono">{wf.trigger}</span>
                      {!wf.enabled && <span className="text-[9px] uppercase tracking-wider text-muted-foreground border px-1 rounded">disabled</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {wf.conditions?.length || 0} condition(s) → {wf.actions?.length || 0} action(s) · {wf.run_count || 0} runs
                    </div>
                    {wf.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{wf.description}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => testRun(wf)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Test run" data-testid={"wf-test-" + wf.id}>
                      <Play size={13} weight="duotone" />
                    </button>
                    <button onClick={() => openRuns(wf)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Show runs" data-testid={"wf-runs-" + wf.id}>
                      <Clock size={13} weight="duotone" />
                    </button>
                    <button onClick={() => toggle(wf)} className="p-1.5 rounded hover:bg-secondary" title={wf.enabled ? "Disable" : "Enable"} data-testid={"wf-toggle-" + wf.id}>
                      <Power size={13} weight="duotone" className={wf.enabled ? "text-emerald-600" : "text-muted-foreground"} />
                    </button>
                    <button onClick={() => { setEditing(wf); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"wf-edit-" + wf.id}>
                      <PencilSimple size={13} weight="duotone" />
                    </button>
                    <button onClick={() => remove(wf)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"wf-delete-" + wf.id}>
                      <Trash size={13} weight="duotone" />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t space-y-1.5">
                    <div className="overline text-muted-foreground">Recent runs</div>
                    {wfRuns.length === 0 && <div className="text-xs text-muted-foreground italic">No runs yet.</div>}
                    {wfRuns.slice(0, 6).map((r) => (
                      <div key={r.id} className="text-xs flex items-center gap-2 py-1">
                        {statusIcon(r.status)}
                        <span className="font-mono text-[10px]">{r.id.slice(0, 8)}</span>
                        <span className="capitalize">{r.status.replace("_", " ")}</span>
                        <span className="text-muted-foreground">· {r.actions_run?.length || 0} actions · {new Date(r.created_at).toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!workflows.length && (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <Lightning size={28} weight="duotone" className="mx-auto mb-2 opacity-50" />
              No workflows yet — click “New workflow” to automate your first flow.
            </div>
          )}
        </div>
      )}
      <WorkflowDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSave={save} />
    </Card>
  );
}
